/**
 * WORKAROUND: keep SIM card add-ons on offer change.
 *
 * Salesforce associations for the SIM card add-ons (group `SIM-kortti`) lack `cspmb__max__c`, so the min/max logic in
 * `handleChangeOffer` treats the group as full and moves the subscription's SIM to `addOnsToRemove`. Hidden SIMs are
 * removed regardless of max.
 *
 * Rule: a SIM add-on the subscription has is moved to `addOnsToUpdate` when the target product has an association for
 * the same add-on and does not exclude it (`Exclusion` dependency). Active flags are ignored.
 *
 * To remove this rule: delete this file, `keepSimAddOnsOnOfferChange.test.ts`,
 * `test/e2e/tests/noeSubscriptionChangeOfferSimAddOns.test.ts`, and the call in `handleChangeOffer`
 * (subscriptionChangeConfirmationUtils.ts).
 */
import { getAddedAddOn } from '../SubscriptionDetails/addOnDependencyUtils.js';
import type { ChangeOfferAddons } from './subscriptionChangeConfirmationUtils.js';
import type { AddedAddon } from '../../common/types/addon.js';
import type { AddOn } from '../../generated/api/addOn.js';
import type { CommercialProduct } from '../../generated/api/commercialProduct.js';
import type {
  AssociationRecord,
  DependencyRecord,
} from '../../legacy/cloudsense-add-on-dependency-engine/AddOnManagerOnline';

const SIM_CARD_ADDON_GROUP = 'SIM-kortti';

export interface OfferChangeData {
  currentProductAssociations: ReadonlyArray<AssociationRecord>;
  currentProductAddOns: ReadonlyArray<AddOn>;
  newProductAssociations: ReadonlyArray<AssociationRecord>;
  dependencies: ReadonlyArray<DependencyRecord>;
  targetProductId: CommercialProduct['commercialProductId'];
}

const isExcludedByTargetProduct = (
  { dependencies, targetProductId }: OfferChangeData,
  assoc: AssociationRecord
): boolean =>
  dependencies.some(
    dep =>
      dep.price_item__c === targetProductId &&
      dep.add_on_target__r?.id === assoc.cspmb__add_on_price_item__r.id &&
      dep.type__c === 'Exclusion'
  );

const getSimAddOnsToKeep = (offerChangeData: OfferChangeData): AddedAddon[] =>
  offerChangeData.currentProductAssociations
    .filter(assoc => {
      const addOnCode = assoc.cspmb__add_on_price_item__r.guid__c;
      return (
        assoc.cspmb__group__c === SIM_CARD_ADDON_GROUP &&
        offerChangeData.currentProductAddOns.some(addOn => addOn.addOnCode === addOnCode) &&
        offerChangeData.newProductAssociations.some(
          newAssoc => newAssoc.cspmb__add_on_price_item__r.guid__c === addOnCode
        ) &&
        !isExcludedByTargetProduct(offerChangeData, assoc)
      );
    })
    .map(assoc => getAddedAddOn(assoc));

export const keepSimAddOnsOnOfferChange = (
  changeOfferAddOns: ChangeOfferAddons,
  offerChangeData: OfferChangeData
): ChangeOfferAddons => {
  const simAddOnsToKeep = getSimAddOnsToKeep(offerChangeData);
  if (simAddOnsToKeep.length === 0) {
    return changeOfferAddOns;
  }
  const simCodesToKeep = new Set(simAddOnsToKeep.map(addOn => addOn.addOnCode));
  const isNotKeptSim = (addOn: AddedAddon) => !simCodesToKeep.has(addOn.addOnCode);

  const addOnsToUpdate = [...changeOfferAddOns.addOnsToUpdate];
  simAddOnsToKeep.forEach(addOn => {
    if (addOnsToUpdate.every(({ addOnCode }) => addOnCode !== addOn.addOnCode)) {
      addOnsToUpdate.push(addOn);
    }
  });

  return {
    addOnsToAdd: changeOfferAddOns.addOnsToAdd.filter(isNotKeptSim),
    addOnsToRemove: changeOfferAddOns.addOnsToRemove.filter(isNotKeptSim),
    addOnsToUpdate,
  };
};
